import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAdminAuth } from "../context/AdminAuthContext";
import {
    fetchUserAnalytics, fetchRevenue, fetchCouponAnalytics,
    fetchFraudAlerts, fetchModerationQueue,
    fetchMLChurn, fetchMLSentiment, fetchMLForecast,
    approveEntity, rejectEntity, setPeriod,
    deleteUser, deleteVendor, deleteBusiness, deleteCoupon,
    approveReview,
} from "../state/slices/adminSlice";
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ComposedChart, Line,
} from "recharts";

// ── Palette ───────────────────────────────────────────────────────────
const C = {
    blue:   "#2575fc",
    green:  "#10b981",
    amber:  "#f59e0b",
    red:    "#ef4444",
    purple: "#8b5cf6",
    teal:   "#14b8a6",
    pink:   "#ec4899",
    gray:   "#94a3b8",
    orange: "#f97316",
};
const PIE_COLORS = [C.blue, C.green, C.amber, C.red, C.purple, C.teal, C.pink, C.orange];

const SIDEBAR_W = 230;

const SECTIONS = [
    { id: "overview",      icon: "ph-squares-four",      label: "Overview" },
    { id: "users",         icon: "ph-users",             label: "Users" },
    { id: "vendors",       icon: "ph-storefront",        label: "Vendors" },
    { id: "coupons",       icon: "ph-tag",               label: "Coupons" },
    { id: "redemptions",   icon: "ph-ticket",            label: "Redemptions" },
    { id: "revenue",       icon: "ph-currency-dollar",   label: "Revenue" },
    { id: "subscriptions", icon: "ph-credit-card",       label: "Subscriptions" },
    { id: "ratings",       icon: "ph-star",              label: "Ratings" },
    { id: "geographic",    icon: "ph-map-pin",           label: "Geographic" },
    { id: "activity",      icon: "ph-activity",          label: "Activity Feed" },
    { id: "moderation",    icon: "ph-shield-warning",    label: "Moderation" },
    { id: "fraud",         icon: "ph-warning",           label: "Fraud Detection" },
    { id: "ml",            icon: "ph-brain",             label: "ML Insights" },
];

// ── Shared primitives ─────────────────────────────────────────────────
const Spinner = ({ size = 40 }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: size * 4 }}>
        <div style={{ width: size, height: size, border: "3px solid #f0f0f0", borderTop: `3px solid ${C.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
);

const Card = ({ children, style = {} }) => (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #ebebeb", boxShadow: "0 2px 12px rgba(0,0,0,0.04)", padding: 24, ...style }}>
        {children}
    </div>
);

const SectionTitle = ({ icon, label, badge }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <i className={`ph-fill ${icon}`} style={{ fontSize: 22, color: C.blue }} />
        <h5 style={{ margin: 0, fontWeight: 700, fontSize: 18, color: "#1a1a1a" }}>{label}</h5>
        {badge != null && badge > 0 && (
            <span style={{ background: C.red, color: "#fff", borderRadius: 99, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{badge}</span>
        )}
    </div>
);

const KpiCard = ({ label, value, icon, color = C.blue, sub }) => (
    <Card style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
            <span style={{ width: 34, height: 34, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`ph-fill ${icon}`} style={{ fontSize: 17, color }} />
            </span>
        </div>
        <p style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#1a1a1a", lineHeight: 1 }}>{value ?? "—"}</p>
        {sub && <span style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</span>}
    </Card>
);

const ChartCard = ({ title, children, height = 260, action }) => (
    <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>{title}</p>
            {action}
        </div>
        <div style={{ height }}>{children}</div>
    </Card>
);

const Table = ({ headers, rows, emptyMsg = "No data" }) => (
    <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
                <tr style={{ borderBottom: "2px solid #f5f5f5" }}>
                    {headers.map((h, i) => (
                        <th key={i} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.length === 0
                    ? <tr><td colSpan={headers.length} style={{ textAlign: "center", padding: 32, color: "#bbb" }}>{emptyMsg}</td></tr>
                    : rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f8f8f8" }}>
                            {row.map((cell, j) => <td key={j} style={{ padding: "10px 12px", color: "#374151", verticalAlign: "middle" }}>{cell}</td>)}
                        </tr>
                    ))
                }
            </tbody>
        </table>
    </div>
);

const Badge = ({ label, color = C.green }) => (
    <span style={{ padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, background: `${color}18`, color }}>{label}</span>
);

const SeverityBadge = ({ level }) => {
    const map = { critical: C.red, high: C.orange, medium: C.amber, low: C.green };
    return <Badge label={level} color={map[level] || C.gray} />;
};

const TOOLTIP = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: "#fff", border: "1px solid #ebebeb", borderRadius: 8, padding: "8px 14px", fontSize: 13, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
            <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#374151" }}>{label}</p>
            {payload.map((p, i) => <p key={i} style={{ margin: 0, color: p.color }}>{p.name}: <strong>{p.value}</strong></p>)}
        </div>
    );
};

const PeriodSelector = ({ value, onChange }) => (
    <div style={{ display: "flex", gap: 3 }}>
        {["7d", "30d", "90d", "6m", "1y"].map(p => (
            <button key={p} onClick={() => onChange(p)} style={{
                padding: "4px 10px", border: "1px solid", fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: 5,
                borderColor: value === p ? C.blue : "#e2e8f0",
                background: value === p ? C.blue : "#fff",
                color: value === p ? "#fff" : "#64748b",
            }}>{p.toUpperCase()}</button>
        ))}
    </div>
);

// ── Heatmap (Phase 4) ─────────────────────────────────────────────────
const DAYS_SHORT  = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const HeatmapGrid = ({ byDay, byHour }) => {
    const dayMap  = Object.fromEntries((byDay  || []).map(d => [d.day,  d.count]));
    const hourMap = Object.fromEntries((byHour || []).map(h => [h.hour, h.count]));
    const maxDay  = Math.max(...Object.values(dayMap),  1);
    const maxHour = Math.max(...Object.values(hourMap), 1);

    const heatColor = (val, max) => {
        const p = Math.min(val / max, 1);
        const r = Math.round(239 * p + 241 * (1 - p));
        const g = Math.round(68  * p + 245 * (1 - p));
        const b = Math.round(68  * p + 251 * (1 - p));
        return `rgb(${r},${g},${b})`;
    };

    return (
        <div>
            <p style={{ fontWeight: 600, fontSize: 13, margin: "0 0 10px", color: "#374151" }}>Redemptions by Day of Week</p>
            <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
                {DAYS_SHORT.map(d => (
                    <div key={d} style={{
                        flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                        background: heatColor(dayMap[d] || 0, maxDay),
                        color: (dayMap[d] || 0) / maxDay > 0.5 ? "#fff" : "#374151",
                    }}>
                        {d}<br />{dayMap[d] || 0}
                    </div>
                ))}
            </div>
            <p style={{ fontWeight: 600, fontSize: 13, margin: "0 0 8px", color: "#374151" }}>Redemptions by Hour (0–23)</p>
            <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} title={`${h}:00 — ${hourMap[h] || 0} redemptions`} style={{
                        width: "calc(4.16% - 3px)", aspectRatio: "1", borderRadius: 4,
                        background: heatColor(hourMap[h] || 0, maxHour),
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, color: (hourMap[h] || 0) / maxHour > 0.5 ? "#fff" : "#94a3b8",
                    }}>{h}</div>
                ))}
            </div>
        </div>
    );
};

// ── Detail Modal ──────────────────────────────────────────────────────
const DetailModal = ({ item, type, onClose }) => {
    if (!item) return null;

    const Field = ({ label, value }) => value != null && value !== "" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 0", borderBottom: "1px solid #f5f5f5" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
            <span style={{ fontSize: 13, color: "#1a1a1a", wordBreak: "break-word" }}>{value}</span>
        </div>
    ) : null;

    const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

    const fields = () => {
        if (type === "vendor") return (
            <>
                <Field label="Vendor Name"   value={item.name} />
                <Field label="Contact"        value={`${item.first_name} ${item.last_name}`} />
                <Field label="Email"          value={item.email} />
                <Field label="Phone"          value={item.phone_number} />
                <Field label="Website"        value={item.website} />
                <Field label="Description"    value={item.vendor_description} />
                <Field label="Address"        value={[item.address, item.apt_number, item.city, item.state, item.zipcode].filter(Boolean).join(", ")} />
                <Field label="Submitted"      value={fmt(item.created_at)} />
                {item.image && (
                    <div style={{ padding: "8px 0" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Logo</span>
                        <img src={item.image} alt="logo" style={{ display: "block", marginTop: 6, maxHeight: 80, borderRadius: 8, objectFit: "contain" }} />
                    </div>
                )}
                {item.banner_image && (
                    <div style={{ padding: "8px 0" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Banner</span>
                        <img src={item.banner_image} alt="banner" style={{ display: "block", marginTop: 6, width: "100%", maxHeight: 120, borderRadius: 8, objectFit: "cover" }} />
                    </div>
                )}
            </>
        );
        if (type === "business") return (
            <>
                <Field label="Business Name" value={item.name} />
                <Field label="Address"       value={[item.address, item.city, item.zipcode].filter(Boolean).join(", ")} />
                <Field label="Phone"         value={item.phone_number} />
                <Field label="Facebook"      value={item.facebook_url} />
                <Field label="Twitter / X"   value={item.twitter_url} />
                <Field label="Yelp"          value={item.yelp_url} />
                <Field label="Vendor ID"     value={item.vendor_id} />
                <Field label="Submitted"     value={fmt(item.created_at)} />
            </>
        );
        if (type === "coupon") return (
            <>
                <Field label="Title"          value={item.title} />
                <Field label="Description"    value={item.description} />
                <Field label="Type"           value={item.type} />
                <Field label="Category"       value={item.category} />
                <Field label="Savings"        value={`$${item.dollar_savings_amount} (${item.saving_percentage}% off)`} />
                <Field label="Max Redemptions per User" value={item.max_redemptions} />
                <Field label="Active"         value={item.active ? "Yes" : "No"} />
                <Field label="Starts"         value={fmt(item.starts_at)} />
                <Field label="Expires"        value={fmt(item.expires_at)} />
                <Field label="Vendor ID"      value={item.vendor_id} />
                <Field label="Business ID"    value={item.business_id} />
                <Field label="Submitted"      value={fmt(item.created_at)} />
                {item.image_url && (
                    <div style={{ padding: "8px 0" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Image</span>
                        <img src={item.image_url} alt="coupon" style={{ display: "block", marginTop: 6, width: "100%", maxHeight: 160, borderRadius: 8, objectFit: "cover" }} />
                    </div>
                )}
            </>
        );
        if (type === "review") return (
            <>
                <Field label="User"          value={item.user_name} />
                <Field label="Rating"        value={"★".repeat(item.rating) + "☆".repeat(5 - item.rating)} />
                <Field label="Review"        value={item.review || "—"} />
                <Field label="Flag Reason"   value={item.flagged_reason} />
                <Field label="Coupon ID"     value={item.coupon_id} />
                <Field label="Vendor ID"     value={item.vendor_id} />
                <Field label="Submitted"     value={fmt(item.created_at)} />
            </>
        );
        return null;
    };

    const typeLabel = { vendor: "Vendor", business: "Business", coupon: "Coupon", review: "Review" }[type] || type;

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
            onClick={onClose}>
            <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #f0f0f0" }}>
                    <div>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: 15, color: "#1a1a1a" }}>{item.name || item.title || `${typeLabel} #${item.id}`}</p>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{typeLabel} — pending review</span>
                    </div>
                    <button onClick={onClose} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ph ph-x" style={{ fontSize: 16, color: "#64748b" }} />
                    </button>
                </div>
                <div style={{ overflowY: "auto", padding: "4px 24px 24px" }}>
                    {fields()}
                </div>
            </div>
        </div>
    );
};

// ── Moderation table subcomponent ─────────────────────────────────────
const ModerationTable = ({ title, rows, columns, renderRow, onApprove, onReject, onInspect, approveLabel = "Approve", rejectLabel = "Reject" }) => (
    <div style={{ marginBottom: 28 }}>
        <p style={{ fontWeight: 700, fontSize: 14, margin: "0 0 10px" }}>
            {title}
            <span style={{ marginLeft: 8, background: "#fef3c7", color: "#b45309", borderRadius: 4, padding: "2px 8px", fontSize: 11 }}>{rows.length}</span>
        </p>
        {rows.length === 0
            ? <p style={{ color: "#94a3b8", fontSize: 13 }}>Nothing pending.</p>
            : (
                <Table
                    headers={[...columns, "Actions"]}
                    rows={rows.map(row => [
                        ...renderRow(row),
                        <div style={{ display: "flex", gap: 6 }}>
                            {onInspect && (
                                <button onClick={() => onInspect(row)} style={{ padding: "4px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                                    <i className="ph ph-eye" style={{ marginRight: 4 }} />View
                                </button>
                            )}
                            {onApprove && (
                                <button onClick={() => onApprove(row)} style={{ padding: "4px 12px", background: C.green, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                                    {approveLabel}
                                </button>
                            )}
                            <button onClick={() => onReject(row)} style={{ padding: "4px 12px", background: C.red, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                                {rejectLabel}
                            </button>
                        </div>
                    ])}
                />
            )
        }
    </div>
);

// ── Main page ─────────────────────────────────────────────────────────
const AdminDashboardPage = () => {
    const { adminLogout } = useAdminAuth();
    const navigate        = useNavigate();
    const dispatch        = useDispatch();

    const [activeSection, setActiveSection]   = useState("overview");
    const [overviewData,  setOverviewData]    = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(true);
    const [overviewError, setOverviewError]   = useState(null);

    const adminState = useSelector(s => s.admin);
    const period     = adminState.period;

    const handleLogout = () => { adminLogout(); navigate("/funcard-admin-dashboard", { replace: true }); };

    const fetchOverview = () => {
        setOverviewLoading(true);
        setOverviewError(null);
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/analytics`)
            .then(r => r.json())
            .then(d => { setOverviewData(d); setOverviewLoading(false); })
            .catch(() => { setOverviewError("Failed to load analytics."); setOverviewLoading(false); });
    };

    useEffect(() => { fetchOverview(); }, []);

    // Lazy-load section data when section becomes active
    useEffect(() => {
        switch (activeSection) {
            case "users":      dispatch(fetchUserAnalytics(period)); break;
            case "revenue":    dispatch(fetchRevenue()); break;
            case "coupons":    dispatch(fetchCouponAnalytics()); break;
            case "fraud":      dispatch(fetchFraudAlerts()); break;
            case "moderation": dispatch(fetchModerationQueue()); break;
            case "ml":
                dispatch(fetchMLChurn());
                dispatch(fetchMLSentiment());
                dispatch(fetchMLForecast());
                break;
            default: break;
        }
    }, [activeSection]);

    // Re-fetch users when period changes
    useEffect(() => {
        if (activeSection === "users") dispatch(fetchUserAnalytics(period));
    }, [period]);

    const handleNav = (id) => setActiveSection(id);

    const modCount = adminState.moderation.data
        ? Object.values(adminState.moderation.data.counts || {}).reduce((s, v) => s + v, 0)
        : 0;
    const fraudCount = adminState.fraud.data?.summary?.high_severity || 0;

    return (
        <div style={{ display: "flex", minHeight: "100vh", background: "#f4f6fb", fontFamily: "inherit" }}>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .adm-nav { cursor: pointer; display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 500; color: #64748b; transition: background 0.15s, color 0.15s; margin-bottom: 2px; }
                .adm-nav:hover { background: #f1f5f9; color: #1a1a1a; }
                .adm-nav.active { background: #eef4ff; color: #2575fc; font-weight: 700; }
                .adm-tab { padding: 7px 16px; border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; color: #64748b; }
                .adm-tab.active { background: #2575fc; color: #fff; border-color: #2575fc; font-weight: 600; }
            `}</style>

            {/* ── Sidebar ── */}
            <aside style={{ width: SIDEBAR_W, flexShrink: 0, background: "#fff", borderRight: "1px solid #ebebeb", display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100, overflowY: "auto" }}>
                <div style={{ padding: "18px 14px 14px", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.blue, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="ph-fill ph-shield-check" style={{ fontSize: 18, color: "#fff" }} />
                        </div>
                        <div>
                            <p style={{ margin: 0, fontWeight: 800, fontSize: 13, color: "#1a1a1a", lineHeight: 1.2 }}>FunCard Admin</p>
                            <p style={{ margin: 0, fontSize: 10, color: "#94a3b8" }}>Analytics Dashboard</p>
                        </div>
                    </div>
                </div>

                <nav style={{ flex: 1, padding: "10px 8px" }}>
                    {SECTIONS.map(s => (
                        <div key={s.id} className={`adm-nav${activeSection === s.id ? " active" : ""}`} onClick={() => handleNav(s.id)}>
                            <i className={`ph-fill ${s.icon}`} style={{ fontSize: 16, flexShrink: 0 }} />
                            {s.label}
                            {s.id === "moderation" && modCount > 0 && <span style={{ marginLeft: "auto", background: C.amber, color: "#fff", borderRadius: 99, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{modCount}</span>}
                            {s.id === "fraud"      && fraudCount > 0 && <span style={{ marginLeft: "auto", background: C.red,   color: "#fff", borderRadius: 99, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{fraudCount}</span>}
                        </div>
                    ))}
                </nav>

                <div style={{ padding: "10px 8px", borderTop: "1px solid #f0f0f0" }}>
                    <button onClick={handleLogout} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#fef2f2", border: "none", borderRadius: 10, color: C.red, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                        <i className="ph ph-sign-out" style={{ fontSize: 15 }} /> Sign Out
                    </button>
                </div>
            </aside>

            {/* ── Main ── */}
            <main style={{ marginLeft: SIDEBAR_W, flex: 1, padding: "28px 24px", maxWidth: `calc(100vw - ${SIDEBAR_W}px)`, minHeight: "100vh" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                    <div>
                        <h4 style={{ margin: 0, fontWeight: 800, color: "#1a1a1a" }}>{SECTIONS.find(s => s.id === activeSection)?.label}</h4>
                        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>FunCard platform analytics — live data</p>
                    </div>
                    <button onClick={fetchOverview} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: C.blue, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        <i className="ph ph-arrows-clockwise" style={{ fontSize: 14 }} /> Refresh
                    </button>
                </div>

                {overviewError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: C.red, borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>{overviewError}</div>}

                {activeSection === "overview"      && (overviewLoading ? <Spinner /> : overviewData ? <Overview data={overviewData} /> : null)}
                {activeSection === "users"         && <UsersSection overviewData={overviewData} adminState={adminState} dispatch={dispatch} period={period} onRefresh={fetchOverview} />}
                {activeSection === "vendors"       && (overviewLoading ? <Spinner /> : overviewData ? <VendorsSection data={overviewData} dispatch={dispatch} onRefresh={fetchOverview} /> : null)}
                {activeSection === "coupons"       && <CouponsSection overviewData={overviewData} adminState={adminState} dispatch={dispatch} onRefresh={fetchOverview} />}
                {activeSection === "redemptions"   && (overviewLoading ? <Spinner /> : overviewData ? <RedemptionsSection data={overviewData} dispatch={dispatch} onRefresh={fetchOverview} /> : null)}
                {activeSection === "revenue"       && <RevenueSection adminState={adminState} />}
                {activeSection === "subscriptions" && (overviewLoading ? <Spinner /> : overviewData ? <SubscriptionsSection data={overviewData} /> : null)}
                {activeSection === "ratings"       && (overviewLoading ? <Spinner /> : overviewData ? <RatingsSection data={overviewData} /> : null)}
                {activeSection === "geographic"    && (overviewLoading ? <Spinner /> : overviewData ? <GeographicSection data={overviewData} /> : null)}
                {activeSection === "activity"      && <ActivitySection staticData={overviewData} />}
                {activeSection === "moderation"    && <ModerationSection adminState={adminState} dispatch={dispatch} />}
                {activeSection === "fraud"         && <FraudSection adminState={adminState} dispatch={dispatch} />}
                {activeSection === "ml"            && <MLSection adminState={adminState} />}
            </main>
        </div>
    );
};

// ── Overview ──────────────────────────────────────────────────────────
const Overview = ({ data }) => {
    const o = data.overview;
    const kpis = [
        { label: "Total Users",         value: o.total_users,          icon: "ph-users",           color: C.blue },
        { label: "Total Vendors",        value: o.total_vendors,        icon: "ph-storefront",      color: C.purple },
        { label: "Total Businesses",     value: o.total_businesses,     icon: "ph-buildings",       color: C.teal },
        { label: "Total Coupons",        value: o.total_coupons,        icon: "ph-tag",             color: C.amber },
        { label: "Active Coupons",       value: o.active_coupons,       icon: "ph-check-circle",    color: C.green },
        { label: "Total Redemptions",    value: o.total_redemptions,    icon: "ph-ticket",          color: C.pink },
        { label: "Active Subscriptions", value: o.active_subscriptions, icon: "ph-credit-card",     color: C.blue },
        { label: "Subscribed Users",     value: o.subscribed_users,     icon: "ph-user-check",      color: C.green },
        { label: "Total Ratings",        value: o.total_ratings,        icon: "ph-star",            color: C.amber },
        { label: "Total Bookmarks",      value: o.total_bookmarks,      icon: "ph-bookmark-simple", color: C.teal },
        { label: "Featured Coupons",     value: o.featured_coupons,     icon: "ph-crown",           color: C.amber },
        { label: "Cat. Featured",        value: o.cat_featured_coupons, icon: "ph-sparkle",         color: C.purple },
    ];

    const merged = data.user_growth.map(u => ({
        month:       u.month,
        users:       u.count,
        redemptions: (data.redemption_trends.find(x => x.month === u.month) || {}).count || 0,
        vendors:     (data.vendor_growth.find(x => x.month === u.month) || {}).count || 0,
    }));

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 22 }}>
                {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                <ChartCard title="Platform Growth (6 months)">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={merged} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.blue} stopOpacity={0.15} /><stop offset="95%" stopColor={C.blue} stopOpacity={0} /></linearGradient>
                                <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.green} stopOpacity={0.15} /><stop offset="95%" stopColor={C.green} stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="users" name="Users" stroke={C.blue} fill="url(#gU)" strokeWidth={2} dot={false} />
                            <Area type="monotone" dataKey="redemptions" name="Redemptions" stroke={C.green} fill="url(#gR)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Coupon Type Breakdown">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={data.coupon_type_breakdown} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={90} label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                                {data.coupon_type_breakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <ChartCard title="Redemptions by Category">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.redemptions_by_category} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis dataKey="category" type="category" tick={{ fontSize: 11 }} width={80} />
                            <Tooltip content={<TOOLTIP />} />
                            <Bar dataKey="count" name="Redemptions" fill={C.blue} radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Subscriptions (6 months)">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.subscription_trends} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gS" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.purple} stopOpacity={0.15} /><stop offset="95%" stopColor={C.purple} stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} />
                            <Area type="monotone" dataKey="count" name="Subscriptions" stroke={C.purple} fill="url(#gS)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
    );
};

// ── Users (Phase 1 expanded) ──────────────────────────────────────────
const UsersSection = ({ overviewData, adminState, dispatch, period, onRefresh }) => {
    const { data: ud, loading, error } = adminState.users;
    const o = overviewData?.overview || {};

    const handleDeleteUser = async (u) => {
        if (!window.confirm(`Permanently delete user "${u.name}" (${u.email})? This cannot be undone.`)) return;
        await dispatch(deleteUser(u.id));
        dispatch(fetchUserAnalytics(period));
        onRefresh();
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <SectionTitle icon="ph-users" label="User Analytics" />
                <PeriodSelector value={period} onChange={p => dispatch(setPeriod(p))} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
                <KpiCard label="Total Users"         value={o.total_users}          icon="ph-users"      color={C.blue} />
                <KpiCard label="Subscribed"          value={o.subscribed_users}     icon="ph-user-check" color={C.green}
                    sub={o.total_users ? `${Math.round(o.subscribed_users / o.total_users * 100)}% conversion` : ""} />
                <KpiCard label="Active in Period"    value={ud?.overview?.active_users_in_period} icon="ph-lightning" color={C.teal}
                    sub={`Last ${period}`} />
                <KpiCard label="Inactive Subscribers" value={ud?.overview?.inactive_subscribers} icon="ph-user-minus" color={C.amber}
                    sub="No activity 60+ days" />
            </div>

            {loading ? <Spinner /> : error ? <p style={{ color: C.red }}>{error}</p> : ud && (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18, marginBottom: 18 }}>
                        <ChartCard title="User Registrations (6 months)">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={ud.registration_trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="gU2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.blue} stopOpacity={0.15} /><stop offset="95%" stopColor={C.blue} stopOpacity={0} /></linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip content={<TOOLTIP />} />
                                    <Area type="monotone" dataKey="count" name="New Users" stroke={C.blue} fill="url(#gU2)" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Subscription Rate">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={[{ name: "Subscribed", value: o.subscribed_users || 0 }, { name: "Free", value: o.unsubscribed_users || 0 }]}
                                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                                        <Cell fill={C.green} /><Cell fill="#e5e7eb" />
                                    </Pie>
                                    <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <Card style={{ marginBottom: 18 }}>
                        <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Cohort Retention (Registration Month → First Redemption)</p>
                        <Table
                            headers={["Cohort", "Registered", "Retained (Redeemed)", "Retention Rate"]}
                            rows={(ud.cohort_retention || []).map(r => [
                                <span style={{ fontWeight: 600 }}>{r.cohort}</span>,
                                r.registered,
                                r.retained,
                                <span style={{ fontWeight: 700, color: r.retention_rate >= 50 ? C.green : r.retention_rate >= 25 ? C.amber : C.red }}>
                                    {r.retention_rate}%
                                </span>,
                            ])}
                            emptyMsg="No cohort data yet"
                        />
                    </Card>

                    <Card>
                        <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Power Users — Top Redeemers</p>
                        <Table
                            headers={["#", "Name", "Email", "Member Since", "Redemptions", "Bookmarks", "Reviews", "Engagement", "Sub", ""]}
                            rows={(ud.power_users || []).map((u, i) => [
                                <span style={{ fontWeight: 700, color: [C.amber, "#94a3b8", "#cd7f32"][i] ?? "#ccc" }}>#{i + 1}</span>,
                                <span style={{ fontWeight: 600 }}>{u.name}</span>,
                                <span style={{ fontSize: 12, color: "#64748b" }}>{u.email}</span>,
                                <span style={{ fontSize: 12 }}>{u.member_since}</span>,
                                <span style={{ fontWeight: 700, color: C.blue }}>{u.redemptions}</span>,
                                u.bookmarks,
                                u.reviews,
                                <Badge label={u.engagement_score} color={C.purple} />,
                                u.subscribed ? <Badge label="Yes" color={C.green} /> : <Badge label="No" color={C.gray} />,
                                <button onClick={() => handleDeleteUser(u)} style={{ padding: "3px 10px", background: C.red, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Delete</button>,
                            ])}
                        />
                    </Card>
                </>
            )}
        </div>
    );
};

// ── Vendors ───────────────────────────────────────────────────────────
const VendorsSection = ({ data, dispatch, onRefresh }) => {
    const o = data.overview;

    const handleDeleteVendor = async (v) => {
        if (!window.confirm(`Permanently delete vendor "${v.name}" and ALL their businesses, coupons, and redemptions? This cannot be undone.`)) return;
        await dispatch(deleteVendor(v.id));
        onRefresh();
    };

    return (
        <div>
            <SectionTitle icon="ph-storefront" label="Vendor Analytics" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
                <KpiCard label="Total Vendors"   value={o.total_vendors}   icon="ph-storefront" color={C.purple} />
                <KpiCard label="Total Businesses" value={o.total_businesses} icon="ph-buildings"  color={C.teal} />
                <KpiCard label="Total Coupons"   value={o.total_coupons}   icon="ph-tag"        color={C.amber} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                <ChartCard title="Vendor Growth (6 months)">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.vendor_growth} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs><linearGradient id="gV" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.purple} stopOpacity={0.15} /><stop offset="95%" stopColor={C.purple} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} />
                            <Area type="monotone" dataKey="count" name="New Vendors" stroke={C.purple} fill="url(#gV)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Top Vendor Ratings">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.vendor_ratings.slice(0, 8)} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                            <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                            <Tooltip content={<TOOLTIP />} />
                            <Bar dataKey="avg_rating" name="Avg Rating" fill={C.amber} radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
            <Card>
                <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Vendor Leaderboard</p>
                <Table
                    headers={["#", "Vendor", "Email", "Coupons", "Redemptions", "Status", ""]}
                    rows={data.top_vendors.map((v, i) => [
                        <span style={{ fontWeight: 700, color: [C.amber, "#94a3b8", "#cd7f32"][i] ?? "#ccc" }}>#{i + 1}</span>,
                        <span style={{ fontWeight: 600 }}>{v.name}</span>,
                        <span style={{ fontSize: 12, color: "#64748b" }}>{v.email}</span>,
                        v.coupons,
                        <span style={{ fontWeight: 700, color: C.blue }}>{v.redemptions}</span>,
                        v.active ? <Badge label="Active" color={C.green} /> : <Badge label="Inactive" color={C.red} />,
                        <button onClick={() => handleDeleteVendor(v)} style={{ padding: "3px 10px", background: C.red, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Delete</button>,
                    ])}
                />
            </Card>
        </div>
    );
};

// ── Coupons (Phase 1 expanded) ────────────────────────────────────────
const CouponsSection = ({ overviewData, adminState, dispatch, onRefresh }) => {
    const o  = overviewData?.overview || {};
    const { data: cd, loading, error } = adminState.coupons;

    const handleDeleteCoupon = async (c) => {
        if (!window.confirm(`Permanently delete coupon "${c.title}"? All redemptions and bookmarks for this coupon will also be deleted. This cannot be undone.`)) return;
        await dispatch(deleteCoupon(c.id));
        dispatch(fetchCouponAnalytics());
        onRefresh();
    };

    return (
        <div>
            <SectionTitle icon="ph-tag" label="Coupon Analytics" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
                <KpiCard label="Total Coupons"  value={o.total_coupons}        icon="ph-tag"          color={C.amber} />
                <KpiCard label="Active Coupons" value={o.active_coupons}       icon="ph-check-circle" color={C.green} />
                <KpiCard label="Featured"       value={o.featured_coupons}     icon="ph-crown"        color={C.amber} />
                <KpiCard label="Cat. Featured"  value={o.cat_featured_coupons} icon="ph-sparkle"      color={C.purple} />
                <KpiCard label="Expiring Soon"  value={cd?.overview?.expiring_soon_7d} icon="ph-clock" color={C.red} sub="Next 7 days" />
            </div>

            {loading ? <Spinner /> : error ? <p style={{ color: C.red }}>{error}</p> : cd && (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                        <ChartCard title="Category Breakdown — Redemptions">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={cd.category_breakdown} layout="vertical" margin={{ top: 0, right: 10, left: 90, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11 }} />
                                    <YAxis dataKey="category" type="category" tick={{ fontSize: 10 }} width={110} />
                                    <Tooltip content={<TOOLTIP />} />
                                    <Bar dataKey="redemptions" name="Redemptions" fill={C.blue}  radius={[0, 4, 4, 0]} />
                                    <Bar dataKey="bookmarks"   name="Bookmarks"   fill={C.teal}  radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Coupon Type ROI">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={cd.coupon_type_roi} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                    <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip content={<TOOLTIP />} />
                                    <Bar dataKey="redemptions" name="Redemptions" fill={C.blue}  radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="count"       name="Coupons"     fill={C.amber} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    <Card>
                        <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Engagement Leaderboard — Score = Redemptions×3 + Bookmarks×2 + Ratings</p>
                        <Table
                            headers={["#", "Title", "Category", "Type", "Vendor", "Redeemed", "Bookmarks", "Rating", "Score", "Status", ""]}
                            rows={(cd.engagement_leaderboard || []).slice(0, 20).map((c, i) => [
                                <span style={{ fontWeight: 700, color: [C.amber, "#94a3b8", "#cd7f32"][i] ?? "#ccc" }}>#{i + 1}</span>,
                                <span style={{ fontWeight: 600, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={c.title}>{c.title}</span>,
                                <Badge label={c.category} color={C.purple} />,
                                <Badge label={c.type} color={C.teal} />,
                                <span style={{ fontSize: 12 }}>{c.vendor}</span>,
                                <span style={{ fontWeight: 700, color: C.blue }}>{c.redemptions}</span>,
                                c.bookmarks,
                                <span style={{ color: C.amber }}>{c.avg_rating > 0 ? `★${c.avg_rating}` : "—"}</span>,
                                <Badge label={c.engagement_score} color={C.green} />,
                                c.active ? <Badge label="Active" color={C.green} /> : <Badge label="Off" color={C.red} />,
                                <button onClick={() => handleDeleteCoupon(c)} style={{ padding: "3px 10px", background: C.red, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Delete</button>,
                            ])}
                        />
                    </Card>
                </>
            )}
        </div>
    );
};

// ── Redemptions (Phase 4 heatmap added) ──────────────────────────────
const RedemptionsSection = ({ data, dispatch, onRefresh }) => {
    const o = data.overview;

    const handleDeleteBusiness = async (b) => {
        if (!window.confirm(`Permanently delete business "${b.name}" and all its coupons and redemptions? This cannot be undone.`)) return;
        await dispatch(deleteBusiness(b.id));
        onRefresh();
    };

    return (
        <div>
            <SectionTitle icon="ph-ticket" label="Redemption Analytics" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
                <KpiCard label="Total Redemptions" value={o.total_redemptions}             icon="ph-ticket"     color={C.pink} />
                <KpiCard label="Categories"        value={data.redemptions_by_category.length} icon="ph-grid-four" color={C.purple} />
                <KpiCard label="Active Coupons"    value={o.active_coupons}                icon="ph-check-circle" color={C.green} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                <ChartCard title="Redemption Trends (6 months)">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.redemption_trends} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs><linearGradient id="gPink" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.pink} stopOpacity={0.15} /><stop offset="95%" stopColor={C.pink} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} />
                            <Area type="monotone" dataKey="count" name="Redemptions" stroke={C.pink} fill="url(#gPink)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Redemptions by Hour of Day" height={260}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.redemptions_by_hour} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="hour" tickFormatter={h => `${h}h`} tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} labelFormatter={h => `${h}:00`} />
                            <Bar dataKey="count" name="Redemptions" fill={C.teal} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            {/* Phase 4: Heatmap */}
            <Card style={{ marginBottom: 18 }}>
                <HeatmapGrid byDay={data.redemptions_by_day} byHour={data.redemptions_by_hour} />
            </Card>

            <Card>
                <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Top Businesses — By Redemptions</p>
                <Table
                    headers={["#", "Business", "City", "Redemptions", "Status", ""]}
                    rows={data.top_businesses.map((b, i) => [
                        <span style={{ fontWeight: 700, color: [C.amber, "#94a3b8", "#cd7f32"][i] ?? "#ccc" }}>#{i + 1}</span>,
                        <span style={{ fontWeight: 600 }}>{b.name}</span>,
                        b.city,
                        <span style={{ fontWeight: 700, color: C.blue }}>{b.redemptions}</span>,
                        b.active ? <Badge label="Active" color={C.green} /> : <Badge label="Inactive" color={C.red} />,
                        <button onClick={() => handleDeleteBusiness(b)} style={{ padding: "3px 10px", background: C.red, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Delete</button>,
                    ])}
                />
            </Card>
        </div>
    );
};

// ── Revenue (Phase 1) ─────────────────────────────────────────────────
const RevenueSection = ({ adminState }) => {
    const { data: rv, loading, error } = adminState.revenue;

    return (
        <div>
            <SectionTitle icon="ph-currency-dollar" label="Revenue Analytics" />
            {loading ? <Spinner /> : error ? <p style={{ color: C.red }}>{error}</p> : rv ? (
                <>
                    {/* ── Total YTD Revenue Hero ── */}
                    <div style={{ marginBottom: 20 }}>
                        <Card style={{
                            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2575fc 100%)",
                            border: "none",
                            padding: "28px 32px",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                                <div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: 1 }}>
                                        Total YTD Revenue — {rv.ytd_year}
                                    </span>
                                    <p style={{ margin: "6px 0 0", fontSize: 42, fontWeight: 900, color: "#fff", lineHeight: 1 }}>
                                        {rv.ytd_total_display}
                                    </p>
                                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4, display: "block" }}>
                                        All revenue streams year-to-date
                                    </span>
                                </div>
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                    {[
                                        { label: "Subscriptions",     value: rv.ytd_sub_display,          count: rv.ytd_sub_count,            icon: "ph-credit-card",  color: C.green },
                                        { label: "Featured Coupons",  value: rv.ytd_featured_display,     count: rv.ytd_featured_count,       icon: "ph-crown",        color: C.amber },
                                        { label: "Cat. Featured",     value: rv.ytd_cat_featured_display, count: rv.ytd_cat_featured_count,   icon: "ph-sparkle",      color: C.purple },
                                    ].map(({ label, value, count, icon, color }) => (
                                        <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "14px 20px", minWidth: 150 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                                <i className={`ph-fill ${icon}`} style={{ fontSize: 15, color }} />
                                                <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#fff" }}>{value}</p>
                                            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{count} {count === 1 ? "sale" : "sales"}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
                        <KpiCard label="MRR"               value={rv.mrr_display}     icon="ph-currency-dollar" color={C.green} sub="Monthly Recurring Revenue" />
                        <KpiCard label="ARR"               value={rv.arr_display}     icon="ph-trend-up"        color={C.blue}  sub="Annual Recurring Revenue" />
                        <KpiCard label="ARPU"              value={rv.arpu_display}    icon="ph-user"            color={C.teal}  sub="Avg Revenue per User" />
                        <KpiCard label="Active Subs"       value={rv.active_subscriptions} icon="ph-credit-card" color={C.purple} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
                        <KpiCard label="Churn Rate (30d)"  value={`${rv.churn_rate}%`}     icon="ph-trend-down"  color={C.red}   sub="Subscriptions lost" />
                        <KpiCard label="Retention Rate"    value={`${rv.retention_rate}%`} icon="ph-heart"       color={C.green} />
                        <KpiCard label="Churned (30d)"     value={rv.churned_30d}           icon="ph-user-minus"  color={C.orange} />
                        <KpiCard label="Expired Subs"      value={rv.expired_subscriptions} icon="ph-clock-countdown" color={C.gray} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18, marginBottom: 18 }}>
                        <ChartCard title="Monthly Revenue (Last 12 Months)" height={280}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={rv.monthly_revenue} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                    <defs><linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.green} stopOpacity={0.15} /><stop offset="95%" stopColor={C.green} stopOpacity={0} /></linearGradient></defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={v => `$${v.toFixed(0)}`} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                                    <Tooltip content={({ active, payload, label }) => {
                                        if (!active || !payload?.length) return null;
                                        return (
                                            <div style={{ background: "#fff", border: "1px solid #ebebeb", borderRadius: 8, padding: "8px 14px", fontSize: 13, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
                                                <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#374151" }}>{label}</p>
                                                {payload.map((p, i) => (
                                                    <p key={i} style={{ margin: 0, color: p.color }}>
                                                        {p.name}: <strong>{p.name === "Revenue" ? `$${parseFloat(p.value).toFixed(2)}` : p.value}</strong>
                                                    </p>
                                                ))}
                                            </div>
                                        );
                                    }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Area yAxisId="left" type="monotone" dataKey="revenue_dollars" name="Revenue" stroke={C.green} fill="url(#gRev)" strokeWidth={2} />
                                    <Line yAxisId="right" type="monotone" dataKey="new_subscriptions" name="New Subs" stroke={C.blue} strokeWidth={2} dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        <ChartCard title="Subscription Status">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={[
                                            { name: "Active",  value: rv.active_subscriptions },
                                            { name: "Expired", value: rv.expired_subscriptions },
                                        ]}
                                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                    >
                                        <Cell fill={C.green} /><Cell fill="#e5e7eb" />
                                    </Pie>
                                    <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>

                    {rv.provider_breakdown?.length > 0 && (
                        <Card>
                            <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Payment Provider Breakdown</p>
                            <Table
                                headers={["Provider", "Subscriptions", "Share"]}
                                rows={(() => {
                                    const total = rv.provider_breakdown.reduce((s, r) => s + r.count, 0);
                                    return rv.provider_breakdown.map(r => [
                                        <span style={{ fontWeight: 600 }}>{r.provider}</span>,
                                        r.count,
                                        <span style={{ color: C.blue }}>{total ? `${(r.count / total * 100).toFixed(1)}%` : "—"}</span>,
                                    ]);
                                })()}
                            />
                        </Card>
                    )}
                </>
            ) : <p style={{ color: "#94a3b8" }}>Loading revenue data...</p>}
        </div>
    );
};

// ── Subscriptions ─────────────────────────────────────────────────────
const SubscriptionsSection = ({ data }) => {
    const o = data.overview;
    const conversionRate = o.total_users ? Math.round(o.subscribed_users / o.total_users * 100) : 0;
    const activeRate     = o.total_subscriptions ? Math.round(o.active_subscriptions / o.total_subscriptions * 100) : 0;

    return (
        <div>
            <SectionTitle icon="ph-credit-card" label="Subscription Analytics" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
                <KpiCard label="Active Subscriptions" value={o.active_subscriptions} icon="ph-credit-card" color={C.blue} />
                <KpiCard label="Total Subscriptions"  value={o.total_subscriptions}  icon="ph-list-checks" color={C.purple} />
                <KpiCard label="Conversion Rate" value={`${conversionRate}%`} icon="ph-trend-up" color={C.green} sub="Users subscribed" />
                <KpiCard label="Active Rate"     value={`${activeRate}%`}     icon="ph-chart-pie" color={C.teal} sub="Of all subscriptions" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18 }}>
                <ChartCard title="New Subscriptions (6 months)">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.subscription_trends} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs><linearGradient id="gS2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.purple} stopOpacity={0.15} /><stop offset="95%" stopColor={C.purple} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} />
                            <Area type="monotone" dataKey="count" name="New Subscriptions" stroke={C.purple} fill="url(#gS2)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Subscription Status">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={[{ name: "Active", value: o.active_subscriptions }, { name: "Expired", value: o.expired_subscriptions }]}
                                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}
                                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}>
                                <Cell fill={C.green} /><Cell fill="#e5e7eb" />
                            </Pie>
                            <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
    );
};

// ── Ratings ───────────────────────────────────────────────────────────
const RatingsSection = ({ data }) => {
    const o    = data.overview;
    const dist = [1, 2, 3, 4, 5].map(n => ({ star: `${n}★`, count: data.rating_distribution[String(n)] || 0 }));
    return (
        <div>
            <SectionTitle icon="ph-star" label="Ratings & Reviews" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
                <KpiCard label="Total Ratings"   value={o.total_ratings}  icon="ph-star"      color={C.amber} />
                <KpiCard label="Average Rating"  value={data.avg_rating}  icon="ph-star-half" color={C.amber} sub="Out of 5.0" />
                <KpiCard label="Total Bookmarks" value={o.total_bookmarks} icon="ph-bookmark-simple" color={C.teal} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
                <ChartCard title="Rating Distribution">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dist} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                            <XAxis dataKey="star" tick={{ fontSize: 13 }} /><YAxis tick={{ fontSize: 11 }} />
                            <Tooltip content={<TOOLTIP />} />
                            <Bar dataKey="count" name="Reviews" radius={[4, 4, 0, 0]}>
                                {dist.map((_, i) => <Cell key={i} fill={[C.red, C.orange, C.amber, C.green, C.green][i]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Vendor Ratings Leaderboard">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.vendor_ratings.slice(0, 8)} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                            <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                            <Tooltip content={<TOOLTIP />} />
                            <Bar dataKey="avg_rating" name="Avg Rating" fill={C.amber} radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
    );
};

// ── Geographic ────────────────────────────────────────────────────────
const GeographicSection = ({ data }) => (
    <div>
        <SectionTitle icon="ph-map-pin" label="Geographic Analytics" />
        <div style={{ marginBottom: 18 }}>
            <ChartCard title="Redemptions by City — Top 15" height={320}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.geographic} layout="vertical" margin={{ top: 0, right: 10, left: 80, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="city" type="category" tick={{ fontSize: 11 }} width={100} />
                        <Tooltip content={<TOOLTIP />} />
                        <Bar dataKey="count" name="Redemptions" radius={[0, 4, 4, 0]}>
                            {data.geographic.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>
        </div>
        <Card>
            <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>City Performance Table</p>
            <Table
                headers={["#", "City", "Redemptions", "Share"]}
                rows={(() => {
                    const total = data.geographic.reduce((s, c) => s + c.count, 0);
                    return data.geographic.map((c, i) => [
                        <span style={{ fontWeight: 700, color: "#94a3b8" }}>#{i + 1}</span>,
                        <span style={{ fontWeight: 600 }}>{c.city}</span>,
                        <span style={{ fontWeight: 700, color: C.blue }}>{c.count}</span>,
                        <span style={{ color: C.purple }}>{total ? `${(c.count / total * 100).toFixed(1)}%` : "—"}</span>,
                    ]);
                })()}
            />
        </Card>
    </div>
);

// ── Activity Feed (Phase 3: SSE) ──────────────────────────────────────
const ActivitySection = ({ staticData }) => {
    const [liveEvents, setLiveEvents] = useState([]);
    const [sseConnected, setSseConnected] = useState(false);
    const esRef = useRef(null);

    useEffect(() => {
        const es = new EventSource(`${process.env.REACT_APP_API_URL}/api/admin/activity/stream`);
        esRef.current = es;

        es.onopen    = () => setSseConnected(true);
        es.onerror   = () => setSseConnected(false);
        es.onmessage = (e) => {
            try {
                const event = JSON.parse(e.data);
                if (!event.error) {
                    setLiveEvents(prev => [event, ...prev].slice(0, 50));
                }
            } catch {}
        };
        return () => { es.close(); setSseConnected(false); };
    }, []);

    const events = liveEvents.length > 0 ? liveEvents : (staticData?.recent_redemptions || []);

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <SectionTitle icon="ph-activity" label="Live Activity Feed" />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: sseConnected ? C.green : "#ccc" }} />
                    <span style={{ fontSize: 12, color: sseConnected ? C.green : "#94a3b8", fontWeight: 600 }}>
                        {sseConnected ? "Live" : "Polling"}
                    </span>
                </div>
            </div>
            <Card>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 560, overflowY: "auto" }}>
                    {events.length === 0 && <p style={{ color: "#bbb", textAlign: "center", padding: 32 }}>No recent redemptions.</p>}
                    {events.map((r, i) => (
                        <div key={r.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: i === 0 && liveEvents.length > 0 ? "#f0f9ff" : "#f8f9fb", borderRadius: 10, border: "1px solid", borderColor: i === 0 && liveEvents.length > 0 ? "#bae6fd" : "#f0f0f0", transition: "background 0.3s" }}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${C.pink}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <i className="ph-fill ph-ticket" style={{ fontSize: 16, color: C.pink }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#1a1a1a" }}>
                                    {r.user} redeemed <span style={{ color: C.blue }}>{r.coupon}</span>
                                </p>
                                <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                                    {r.business} · {r.city} · <Badge label={r.category} color={C.purple} />
                                </p>
                            </div>
                            <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>
                                {r.redeemed_on ? new Date(r.redeemed_on).toLocaleString() : "—"}
                            </span>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
};

// ── Moderation (Phase 2) ──────────────────────────────────────────────
const ModerationSection = ({ adminState, dispatch }) => {
    const { data: mod, loading, error } = adminState.moderation;
    const [tab, setTab] = useState("vendors");
    const [inspecting, setInspecting] = useState(null);
    const [inspectType, setInspectType] = useState(null);

    const openInspect = (type, row) => { setInspectType(type); setInspecting(row); };
    const closeInspect = () => { setInspecting(null); setInspectType(null); };

    const refresh = () => dispatch(fetchModerationQueue());

    const handleApprove = async (type, row) => {
        await dispatch(approveEntity({ type, id: row.id }));
        closeInspect();
        refresh();
    };

    const handleReject = async (type, row) => {
        const reason = window.prompt(`Rejection reason for "${row.name || row.title}":`);
        if (reason !== null) {
            await dispatch(rejectEntity({ type, id: row.id, reason }));
            closeInspect();
            refresh();
        }
    };

    const totalPending = mod ? Object.values(mod.counts || {}).reduce((s, v) => s + v, 0) : 0;

    return (
        <div>
            <DetailModal item={inspecting} type={inspectType} onClose={closeInspect} />

            <SectionTitle icon="ph-shield-warning" label="Moderation Queue" badge={totalPending} />

            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {[
                    { key: "vendors",    label: "Vendors",    count: mod?.counts?.vendors },
                    { key: "businesses", label: "Businesses", count: mod?.counts?.businesses },
                    { key: "coupons",    label: "Coupons",    count: mod?.counts?.coupons },
                    { key: "reviews",    label: "Reviews",    count: mod?.counts?.reviews },
                ].map(t => (
                    <button key={t.key} className={`adm-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                        {t.label} {t.count != null && t.count > 0 ? <span style={{ marginLeft: 4, background: C.amber, color: "#fff", borderRadius: 99, padding: "1px 6px", fontSize: 10 }}>{t.count}</span> : null}
                    </button>
                ))}
            </div>

            {loading ? <Spinner /> : error ? <p style={{ color: C.red }}>{error}</p> : mod ? (
                <Card>
                    {tab === "vendors" && (
                        <ModerationTable
                            title="Pending Vendor Approvals"
                            rows={mod.pending_vendors || []}
                            columns={["Name", "Email", "City", "Created"]}
                            renderRow={v => [
                                <span style={{ fontWeight: 600 }}>{v.name}</span>,
                                <span style={{ fontSize: 12, color: "#64748b" }}>{v.email}</span>,
                                v.city,
                                <span style={{ fontSize: 12 }}>{v.created_at ? new Date(v.created_at).toLocaleDateString() : "—"}</span>,
                            ]}
                            onInspect={v => openInspect("vendor", v)}
                            onApprove={v => handleApprove("vendor", v)}
                            onReject={v  => handleReject("vendor",  v)}
                        />
                    )}
                    {tab === "businesses" && (
                        <ModerationTable
                            title="Pending Business Approvals"
                            rows={mod.pending_businesses || []}
                            columns={["Name", "City", "Created"]}
                            renderRow={b => [
                                <span style={{ fontWeight: 600 }}>{b.name}</span>,
                                b.city,
                                <span style={{ fontSize: 12 }}>{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</span>,
                            ]}
                            onInspect={b => openInspect("business", b)}
                            onApprove={b => handleApprove("business", b)}
                            onReject={b  => handleReject("business",  b)}
                        />
                    )}
                    {tab === "coupons" && (
                        <ModerationTable
                            title="Pending Coupon Approvals"
                            rows={mod.pending_coupons || []}
                            columns={["Title", "Type", "Category", "Created"]}
                            renderRow={c => [
                                <span style={{ fontWeight: 600, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} title={c.title}>{c.title}</span>,
                                <Badge label={c.type}     color={C.blue} />,
                                <Badge label={c.category} color={C.purple} />,
                                <span style={{ fontSize: 12 }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</span>,
                            ]}
                            onInspect={c => openInspect("coupon", c)}
                            onApprove={c => handleApprove("coupon", c)}
                            onReject={c  => handleReject("coupon",  c)}
                        />
                    )}
                    {tab === "reviews" && (
                        <ModerationTable
                            title="Pending Review Approvals"
                            rows={mod.flagged_reviews || []}
                            columns={["User", "Rating", "Review Text", "Submitted"]}
                            renderRow={r => [
                                <span style={{ fontWeight: 600, fontSize: 12 }}>{r.user_name || `User #${r.user_id}`}</span>,
                                <span style={{ color: C.amber }}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>,
                                <span style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", fontSize: 12 }}>{r.review || "—"}</span>,
                                <span style={{ fontSize: 12 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</span>,
                            ]}
                            onInspect={r => openInspect("review", r)}
                            onApprove={async r => {
                                await dispatch(approveReview(r.id));
                                refresh();
                            }}
                            onReject={r => {
                                if (window.confirm("Delete this review permanently?")) {
                                    fetch(`${process.env.REACT_APP_API_URL}/api/admin/moderation/delete-review/${r.id}`, { method: "DELETE" }).then(refresh);
                                }
                            }}
                            approveLabel="Approve"
                            rejectLabel="Delete"
                        />
                    )}
                </Card>
            ) : <p style={{ color: "#94a3b8" }}>Click a tab to view queue.</p>}
        </div>
    );
};

// ── Fraud Detection (Phase 3) ─────────────────────────────────────────
const FraudSection = ({ adminState, dispatch }) => {
    const { data: fd, loading, error } = adminState.fraud;
    const [tab, setTab] = useState("rapid");

    const summary = fd?.summary || {};

    return (
        <div>
            <SectionTitle icon="ph-warning" label="Fraud Detection" badge={summary.high_severity} />

            {fd && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
                    <KpiCard label="Total Alerts"      value={summary.total_alerts}          icon="ph-warning"           color={C.red}    />
                    <KpiCard label="High Severity"     value={summary.high_severity}         icon="ph-fire"              color={C.red}    />
                    <KpiCard label="Rapid Redemptions" value={summary.rapid_redemptions}     icon="ph-lightning"         color={C.orange} />
                    <KpiCard label="Duplicates"        value={summary.duplicate_redemptions} icon="ph-copy"              color={C.amber}  />
                    <KpiCard label="Spam Reviews"      value={summary.spam_reviews}          icon="ph-chat-centered-dots" color={C.purple} />
                </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {[
                    { key: "rapid",     label: "Rapid Redemptions",  count: summary.rapid_redemptions },
                    { key: "duplicate", label: "Duplicate",          count: summary.duplicate_redemptions },
                    { key: "spam",      label: "Spam Reviews",       count: summary.spam_reviews },
                    { key: "vendors",   label: "Suspicious Vendors", count: summary.suspicious_vendors },
                    { key: "velocity",  label: "High Velocity",      count: summary.high_velocity },
                ].map(t => (
                    <button key={t.key} className={`adm-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                        {t.label} {t.count > 0 ? <span style={{ marginLeft: 4, background: C.red, color: "#fff", borderRadius: 99, padding: "1px 6px", fontSize: 10 }}>{t.count}</span> : null}
                    </button>
                ))}
            </div>

            {loading ? <Spinner /> : error ? <p style={{ color: C.red }}>{error}</p> : fd ? (
                <Card>
                    {tab === "rapid" && (
                        <Table
                            headers={["User", "Email", "Count", "Window", "First", "Last", "Severity"]}
                            rows={(fd.rapid_redemptions || []).map(r => [
                                <span style={{ fontWeight: 600 }}>{r.name}</span>,
                                <span style={{ fontSize: 12 }}>{r.email}</span>,
                                <span style={{ fontWeight: 700, color: C.red }}>{r.count}</span>,
                                `${r.window_hours}h`,
                                r.first_redemption ? new Date(r.first_redemption).toLocaleTimeString() : "—",
                                r.last_redemption  ? new Date(r.last_redemption).toLocaleTimeString()  : "—",
                                <SeverityBadge level={r.severity} />,
                            ])}
                            emptyMsg="No rapid redemption alerts"
                        />
                    )}
                    {tab === "duplicate" && (
                        <Table
                            headers={["User", "Email", "Coupon", "Count", "First Redeemed", "Severity"]}
                            rows={(fd.duplicate_redemptions || []).map(r => [
                                <span style={{ fontWeight: 600 }}>{r.name}</span>,
                                <span style={{ fontSize: 12 }}>{r.email}</span>,
                                <span style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{r.coupon}</span>,
                                <span style={{ fontWeight: 700, color: C.orange }}>{r.count}×</span>,
                                r.first_redemption ? new Date(r.first_redemption).toLocaleDateString() : "—",
                                <SeverityBadge level={r.severity} />,
                            ])}
                            emptyMsg="No duplicate redemptions detected"
                        />
                    )}
                    {tab === "spam" && (
                        <Table
                            headers={["User", "Email", "Reviews (24h)", "Window", "Severity"]}
                            rows={(fd.spam_reviews || []).map(r => [
                                <span style={{ fontWeight: 600 }}>{r.name}</span>,
                                <span style={{ fontSize: 12 }}>{r.email}</span>,
                                <span style={{ fontWeight: 700, color: C.purple }}>{r.review_count}</span>,
                                `${r.window_hours}h`,
                                <SeverityBadge level={r.severity} />,
                            ])}
                            emptyMsg="No spam review alerts"
                        />
                    )}
                    {tab === "vendors" && (
                        <Table
                            headers={["Vendor ID", "Name", "Email", "Redemptions", "Ratings", "Flag"]}
                            rows={(fd.suspicious_vendors || []).map(r => [
                                `#${r.vendor_id}`,
                                <span style={{ fontWeight: 600 }}>{r.name}</span>,
                                <span style={{ fontSize: 12 }}>{r.email}</span>,
                                <span style={{ fontWeight: 700, color: C.blue }}>{r.redemptions}</span>,
                                <span style={{ fontWeight: 700, color: C.red }}>{r.ratings}</span>,
                                <Badge label={r.flag} color={C.orange} />,
                            ])}
                            emptyMsg="No suspicious vendors detected"
                        />
                    )}
                    {tab === "velocity" && (
                        <Table
                            headers={["Coupon", "Count", "Window", "Severity"]}
                            rows={(fd.high_velocity_coupons || []).map(r => [
                                <span style={{ fontWeight: 600, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{r.title}</span>,
                                <span style={{ fontWeight: 700, color: C.red }}>{r.count}</span>,
                                `${r.window_hours}h`,
                                <SeverityBadge level={r.severity} />,
                            ])}
                            emptyMsg="No high-velocity coupons detected"
                        />
                    )}
                </Card>
            ) : null}

            <div style={{ marginTop: 16 }}>
                <button onClick={() => dispatch(fetchFraudAlerts())} style={{ padding: "8px 16px", background: "#fff", border: `1px solid ${C.blue}`, borderRadius: 8, color: C.blue, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                    <i className="ph ph-arrows-clockwise" style={{ marginRight: 6 }} />Refresh Fraud Report
                </button>
            </div>
        </div>
    );
};

// ── ML Insights (Phase 5) ─────────────────────────────────────────────
const MLSection = ({ adminState }) => {
    const { data: churn,     loading: cl } = adminState.ml.churn;
    const { data: sentiment, loading: sl } = adminState.ml.sentiment;
    const { data: forecast,  loading: fl } = adminState.ml.forecast;
    const [tab, setTab] = useState("churn");

    const sentimentPie = sentiment ? [
        { name: "Positive", value: sentiment.positive },
        { name: "Neutral",  value: sentiment.neutral  },
        { name: "Negative", value: sentiment.negative },
    ] : [];

    const forecastData = forecast ? [
        ...(forecast.historical || []).map(d => ({ date: d.date, actual: d.count })),
        ...(forecast.forecast   || []).map(d => ({ date: d.date, forecast: d.predicted, lower: d.lower, upper: d.upper })),
    ] : [];

    return (
        <div>
            <SectionTitle icon="ph-brain" label="ML Insights" />
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 16px", marginBottom: 20, fontSize: 13, color: "#92400e" }}>
                <i className="ph ph-info" style={{ marginRight: 6 }} />
                ML features require additional packages. Check <strong>/admin/ml/status</strong> for dependency availability.
                Install: <code>pip install scikit-learn vaderSentiment prophet</code>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {[
                    { key: "churn",     label: "Churn Prediction" },
                    { key: "sentiment", label: "Sentiment Analysis" },
                    { key: "forecast",  label: "Demand Forecast" },
                ].map(t => (
                    <button key={t.key} className={`adm-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "churn" && (
                cl ? <Spinner /> : churn ? (
                    churn.error ? <p style={{ color: C.orange, fontSize: 14 }}>{churn.error}</p> : (
                        <>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 18 }}>
                                <KpiCard label="Total Users Analyzed" value={churn.total_users}   icon="ph-users"    color={C.blue} />
                                <KpiCard label="High Churn Risk"       value={churn.at_risk_high}  icon="ph-warning"  color={C.red}  sub="≥70% churn probability" />
                                <KpiCard label="Model"                 value={churn.model}         icon="ph-brain"    color={C.purple} />
                            </div>
                            <Card>
                                <p style={{ margin: "0 0 14px", fontWeight: 700, fontSize: 14 }}>Churn Risk Rankings — Top At-Risk Users</p>
                                <Table
                                    headers={["User", "Email", "Subscribed", "Churn Prob.", "Risk Level", "Days Since Redeemed", "Total Redeemed"]}
                                    rows={(churn.predictions || []).map(u => [
                                        <span style={{ fontWeight: 600 }}>{u.name}</span>,
                                        <span style={{ fontSize: 12 }}>{u.email}</span>,
                                        u.subscribed ? <Badge label="Yes" color={C.green} /> : <Badge label="No" color={C.gray} />,
                                        <span style={{ fontWeight: 700, color: u.churn_probability >= 0.7 ? C.red : u.churn_probability >= 0.4 ? C.amber : C.green }}>
                                            {(u.churn_probability * 100).toFixed(1)}%
                                        </span>,
                                        <SeverityBadge level={u.risk_level} />,
                                        u.days_since_last_redemption,
                                        u.total_redemptions,
                                    ])}
                                />
                            </Card>
                        </>
                    )
                ) : <p style={{ color: "#94a3b8" }}>Fetching churn predictions...</p>
            )}

            {tab === "sentiment" && (
                sl ? <Spinner /> : sentiment ? (
                    sentiment.total_reviews === 0
                        ? <Card><p style={{ textAlign: "center", color: "#94a3b8", padding: 32 }}>No reviews with text to analyze yet.</p></Card>
                        : (
                            <>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 18 }}>
                                    <KpiCard label="Total Reviews"   value={sentiment.total_reviews}  icon="ph-chat-centered-dots" color={C.blue} />
                                    <KpiCard label="Positive"        value={`${sentiment.positive_pct}%`} icon="ph-smiley"         color={C.green}  sub={`${sentiment.positive} reviews`} />
                                    <KpiCard label="Neutral"         value={`${sentiment.neutral_pct}%`}  icon="ph-smiley-meh"    color={C.amber}  sub={`${sentiment.neutral} reviews`} />
                                    <KpiCard label="Negative"        value={`${sentiment.negative_pct}%`} icon="ph-smiley-sad"    color={C.red}    sub={`${sentiment.negative} reviews`} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
                                    <ChartCard title="Sentiment Distribution">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={sentimentPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                                                    <Cell fill={C.green} /><Cell fill={C.amber} /><Cell fill={C.red} />
                                                </Pie>
                                                <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                    <Card>
                                        <p style={{ fontWeight: 700, fontSize: 14, margin: "0 0 12px" }}>Method: <Badge label={sentiment.method} color={C.teal} /></p>
                                        <p style={{ fontWeight: 600, fontSize: 13, margin: "0 0 8px", color: C.red }}>Sample Negative Reviews</p>
                                        {(sentiment.samples?.negative || []).map((s, i) => (
                                            <div key={i} style={{ background: "#fef2f2", borderRadius: 8, padding: "8px 12px", marginBottom: 6, fontSize: 12 }}>
                                                <span style={{ color: C.amber }}>{"★".repeat(s.rating)}</span> — {s.review || "(no text)"}
                                            </div>
                                        ))}
                                    </Card>
                                </div>
                            </>
                        )
                ) : <p style={{ color: "#94a3b8" }}>Fetching sentiment data...</p>
            )}

            {tab === "forecast" && (
                fl ? <Spinner /> : forecast ? (
                    forecast.error ? <p style={{ color: C.orange, fontSize: 14 }}>{forecast.error}</p> : (
                        <>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 18 }}>
                                <KpiCard label="Method"         value={forecast.method}         icon="ph-brain"  color={C.purple} />
                                <KpiCard label="Avg (Last 7d)"  value={forecast.avg_last_7d}   icon="ph-trend-up" color={C.blue}  sub="redemptions/day" />
                                <KpiCard label="Avg (Last 30d)" value={forecast.avg_last_30d}  icon="ph-calendar" color={C.teal}  sub="redemptions/day" />
                            </div>
                            <ChartCard title="Demand Forecast — Historical + 30-Day Projection" height={320}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={forecastData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                        <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={6} />
                                        <YAxis tick={{ fontSize: 11 }} />
                                        <Tooltip content={<TOOLTIP />} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                        <Bar dataKey="actual"   name="Actual"   fill={C.blue}  opacity={0.7} />
                                        <Line type="monotone" dataKey="forecast" name="Forecast" stroke={C.red} strokeWidth={2} strokeDasharray="5 5" dot={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </>
                    )
                ) : <p style={{ color: "#94a3b8" }}>Fetching forecast data...</p>
            )}
        </div>
    );
};

export default AdminDashboardPage;
